import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";

const START_FEN =
  "rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1";

const STOCKFISH_JS = "/stockfish/src/stockfish-nnue-16-single.js";
const STOCKFISH_WASM = "/stockfish/src/stockfish-nnue-16-single.wasm";

function buildBoardHtml() {
  return String.raw`<!doctype html>
<html>
<head>
  <meta charset="utf-8" />
  <meta http-equiv="X-UA-Compatible" content="IE=edge,chrome=1" />
  <meta name="viewport" content="width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no" />
  <script src="https://cdnjs.cloudflare.com/ajax/libs/chess.js/0.12.0/chess.min.js"></script>
  <link rel="stylesheet" href="https://unpkg.com/@chrisoakman/chessboardjs@1.0.0/dist/chessboard-1.0.0.min.css" />
  <style>
    /* Keep this geometry aligned with the mobile HoodEngineBoard / Training board. */
    :root { --gap: 4px; --label-size: 12px; --file-band: 18px; --rank-band: 18px; }
    html, body { width:100%; height:100%; }
    body { margin:0; background:transparent !important; color:#fff; overflow:hidden; user-select:none; -webkit-user-select:none; -webkit-touch-callout:none; }
    .outer { width:100%; height:100%; display:grid; grid-template-rows:var(--file-band) 1fr var(--file-band); grid-template-columns:var(--rank-band) 1fr var(--rank-band); align-items:center; justify-items:center; background:transparent !important; box-sizing:border-box; }
    .files { display:grid; grid-template-columns:repeat(8,1fr); width:100%; height:100%; padding:0 var(--gap); box-sizing:border-box; }
    .files span { font-size:var(--label-size); opacity:.95; text-align:center; line-height:1; user-select:none; }
    #file-bottom { transform:translateY(-1%); }
    .ranks { display:grid; grid-template-rows:repeat(8,1fr); width:100%; height:100%; padding:var(--gap) 0; box-sizing:border-box; }
    .ranks span { font-size:var(--label-size); opacity:.95; display:flex; align-items:center; justify-content:center; user-select:none; }
    #boardWrap { width:min(calc(100vw - (2 * var(--rank-band)) - (2 * var(--gap))),900px); aspect-ratio:1/1; background:transparent !important; padding:var(--gap); box-sizing:border-box; margin:0 auto; position:relative; top:-3vh; }
    @media (max-width: 768px) {
      :root { --gap:0px; --file-band:0px; --rank-band:0px; --label-size:12px; }
      html,body { width:100%; height:100%; overflow:hidden; }
      .outer {
        position:relative;
        display:block;
        width:100vw;
        height:100vw;
        min-width:100vw;
        min-height:100vw;
      }
      #boardWrap {
        position:absolute;
        inset:0;
        width:100%;
        height:100%;
        max-width:none;
        aspect-ratio:auto;
        padding:0;
        margin:0;
        top:0;
      }
      #file-top,#rank-right { display:none!important; }
      #file-bottom {
        position:absolute;
        left:0;
        right:0;
        bottom:2px;
        width:100%;
        height:18px;
        z-index:35;
        transform:none;
        padding:0 2px;
        pointer-events:none;
      }
      #rank-left {
        position:absolute;
        left:2px;
        top:0;
        bottom:0;
        width:18px;
        height:100%;
        z-index:35;
        display:grid!important;
        padding:2px 0;
        pointer-events:none;
      }
      .files span,.ranks span {
        color:#111!important;
        opacity:1!important;
        font-weight:900!important;
        font-size:12px!important;
        text-shadow:0 1px 1px rgba(255,255,255,.85);
      }
      #engineState { right:4px; bottom:22px; }
    }
    #myBoard { width:100%; height:100%; margin:0; transform:translateZ(0); }
    .square-55d63.selected { outline:3px solid #FFD700; outline-offset:-3px; }
    .square-55d63.target { box-shadow:inset 0 0 0 4px rgba(255,215,0,.45); }
    #promoOverlay { position:fixed; inset:0; background:rgba(0,0,0,.55); display:none; align-items:center; justify-content:center; z-index:9999; }
    #promoOverlay.show { display:flex; }
    .promoBox { background:#222; border-radius:14px; padding:14px 16px; display:flex; gap:14px; align-items:center; }
    .promoBox img { width:min(20vw,90px); height:min(20vw,90px); object-fit:contain; cursor:pointer; border-radius:10px; padding:6px; background:#ddded8; transition:transform .08s; }
    .promoBox img:active { transform:scale(.96); }
    #engineState { position:absolute; inset:auto 6px 6px auto; z-index:20; border-radius:999px; padding:4px 7px; background:rgba(2,8,23,.72); color:#cbd5e1; font:700 10px/1.2 Arial,sans-serif; pointer-events:none; }
  </style>
</head>
<body>
  <div class="outer">
    <div></div><div id="file-top" class="files"></div><div></div>
    <div id="rank-left" class="ranks"></div>
    <div id="boardWrap"><div id="myBoard"></div><div id="engineState">Stockfish…</div></div>
    <div id="rank-right" class="ranks"></div>
    <div></div><div id="file-bottom" class="files"></div><div></div>
  </div>
  <div id="promoOverlay"><div class="promoBox">
    <img data-piece="q" alt="Queen" />
    <img data-piece="r" alt="Rook" />
    <img data-piece="b" alt="Bishop" />
    <img data-piece="n" alt="Knight" />
  </div></div>

  <script src="https://code.jquery.com/jquery-3.5.1.min.js"></script>
  <script src="https://unpkg.com/@chrisoakman/chessboardjs@1.0.0/dist/chessboard-1.0.0.min.js"></script>
  <script>
  (function(){
    var START_FEN = '${START_FEN}';
    var STOCKFISH_JS = '${STOCKFISH_JS}';
    var STOCKFISH_WASM = '${STOCKFISH_WASM}';
    var PIECE_BASE = 'https://chessboardjs.com/img/chesspieces/wikipedia/';
    var IS_TOUCH = ('ontouchstart' in window) || (navigator.maxTouchPoints && navigator.maxTouchPoints > 0);

    function post(msg){
      try { parent.postMessage({ __hoodBoard: true, payload: msg }, '*'); } catch(e) {}
    }
    function setEngineState(text){
      var el = document.getElementById('engineState');
      if(el) el.textContent = text || '';
    }
    function renderFiles(id){
      var order = userColor === 'w' ? ['a','b','c','d','e','f','g','h'] : ['h','g','f','e','d','c','b','a'];
      var el = document.getElementById(id);
      if(el) el.innerHTML = order.map(function(x){ return '<span>'+x+'</span>'; }).join('');
    }
    function renderRanks(id){
      var order = userColor === 'w' ? ['8','7','6','5','4','3','2','1'] : ['1','2','3','4','5','6','7','8'];
      var el = document.getElementById(id);
      if(el) el.innerHTML = order.map(function(x){ return '<span>'+x+'</span>'; }).join('');
    }
    function refreshCoords(){ renderFiles('file-top'); renderFiles('file-bottom'); renderRanks('rank-left'); renderRanks('rank-right'); }
    function resizeVisualBoard(){ try { board.resize(); } catch(e) {} }
    function currentPgn(){
      try { return String(game.pgn() || '').replace(/[\r\n]+/g,' ').replace(/\s+/g,' ').trim(); } catch(e) { return ''; }
    }
    function currentPly(){ try { return (game.history() || []).length; } catch(e) { return 0; } }
    function postTurn(){ post({ type:'turn', turn:game.turn(), fen:game.fen(), pgn:currentPgn(), ply:currentPly(), userColor:userColor }); }
    function postThinking(v){ post({ type:'thinking', value:!!v }); }
    function postGameOverIfAny(){
      try {
        if(game.in_checkmate && game.in_checkmate()) { post({ type:'gameOver', reason:game.turn()==='w'?'black_wins':'white_wins', fen:game.fen(), pgn:currentPgn(), ply:currentPly(), userColor:userColor }); return true; }
        if((game.in_stalemate && game.in_stalemate()) || (game.in_draw && game.in_draw())) { post({ type:'gameOver', reason:'draw', fen:game.fen(), pgn:currentPgn(), ply:currentPly(), userColor:userColor }); return true; }
      } catch(e) {}
      return false;
    }

    var engine = null;
    var engineReady = false;
    var userColor = 'w';
    var skillLevel = 7;
    var playElo = 1600;
    var multiPV = 1;
    var game = new Chess(START_FEN);
    var mode = 'idle';
    var analysisTurn = 'w';
    var infoBuffer = {};
    var lastUserTurnEval = null;
    var pendingUserMoveSan = '';
    var waitingForBestmove = false;
    var pendingNext = null;
    var staleBestmoves = 0;
    var candidateCtx = null;
    var bestmoveWatchdog = null;
    var watchdogRetries = 0;
    var coachReplyArmed = false;
    var coachReplyReleased = false;
    var coachReplyTimer = null;
    var coachUserMoveTs = 0;
    var practiceActive = false;
    var practiceLine = [];
    var practicePtr = 0;
    var practiceInBook = false;
    var wrongCandidateCtx = null;
    var redoStack = [];
    var lastEmitTs = 0;

    var PLAY_MOVETIME_MS = 1000;
    var PLAY_MAX_DEPTH = 6;
    var ANALYSIS_DEPTH = 12;
    var ANALYSIS_MOVETIME_MS = 2200;
    var BESTMOVE_TIMEOUT_MS = 7000;
    var MAX_WATCHDOG_RETRIES = 1;
    var COACH_WINDOW_MS = 12000;
    var COACH_MIN_REPLY_MS = 700;

    function setOption(name,value){ if(engine) engine.postMessage('setoption name '+name+' value '+value); }
    function posFen(){ if(engine) engine.postMessage('position fen '+game.fen()); }
    function stopEngine(){ try { if(engine) engine.postMessage('stop'); } catch(e) {} }

    function pvToSanArray(fenBefore,pvStr,max){
      if(!pvStr) return [];
      var tokens = String(pvStr).trim().split(/\s+/).slice(0,max||6);
      var out = [];
      try {
        var c = new Chess(fenBefore);
        tokens.forEach(function(uci){
          if(!uci || uci.length<4) return;
          var mv = c.move({from:uci.slice(0,2),to:uci.slice(2,4),promotion:uci.length>=5?uci[4]:'q'});
          if(mv) out.push(mv.san);
        });
      } catch(e) {}
      return out;
    }

    function emitAnalysis(force,isFinal){
      var now = Date.now();
      if(!force && now-lastEmitTs<220) return;
      lastEmitTs = now;
      var fenBefore = game.fen();
      var keys = Object.keys(infoBuffer).map(function(n){return parseInt(n,10);}).filter(function(n){return n>=1&&n<=3;}).sort(function(a,b){return a-b;});
      var lines = [];
      var maxDepth = 0;
      keys.forEach(function(k){
        var info = infoBuffer[k]; if(!info) return;
        maxDepth = Math.max(maxDepth,info.depth||0);
        var cp=info.cp, mate=info.mate;
        if(analysisTurn==='b'){ if(cp!=null) cp=-cp; if(mate!=null) mate=-mate; }
        lines.push({rank:k,cp:cp,mate:mate,pv:pvToSanArray(fenBefore,info.pvUci,6)});
      });
      if(lines.length) post({type:'analysis',depth:maxDepth,lines:lines,fen:fenBefore,pgn:currentPgn(),ply:currentPly(),turn:game.turn(),userColor:userColor,final:!!isFinal});
    }

    function clearWatchdog(){ if(bestmoveWatchdog){ clearTimeout(bestmoveWatchdog); bestmoveWatchdog=null; } }
    function clearCoachReply(){
      if(coachReplyTimer){ clearTimeout(coachReplyTimer); coachReplyTimer=null; }
      coachReplyArmed=false; coachReplyReleased=false;
    }
    function hardResetSearchState(){
      if(waitingForBestmove) staleBestmoves++;
      pendingNext=null; candidateCtx=null; waitingForBestmove=false; watchdogRetries=0;
      clearWatchdog(); clearCoachReply(); stopEngine(); postThinking(false); mode='idle'; infoBuffer={};
    }
    function armWatchdog(kind){
      clearWatchdog();
      bestmoveWatchdog=setTimeout(function(){
        bestmoveWatchdog=null;
        if(!waitingForBestmove) return;
        waitingForBestmove=false;
        if(pendingNext){ watchdogRetries=0; flushPendingNext(); return; }
        if(watchdogRetries>=MAX_WATCHDOG_RETRIES){
          watchdogRetries=0;
          if(kind==='play') { postThinking(false); mode='idle'; post({type:'engineError',message:'Engine timed out — tap Restart.'}); ensureEngineToMove(); }
          else doStartAnalysis();
          return;
        }
        watchdogRetries++;
        if(kind==='play'){ postThinking(false); doStartEngineMove(); } else doStartAnalysis();
      },BESTMOVE_TIMEOUT_MS);
    }

    function doStartAnalysis(){
      if(!engineReady) return;
      mode='analyze'; analysisTurn=game.turn(); infoBuffer={};
      setOption('UCI_LimitStrength','false'); setOption('Skill Level',20); setOption('MultiPV',multiPV); posFen();
      waitingForBestmove=true; armWatchdog('analyze');
      engine.postMessage('go depth '+ANALYSIS_DEPTH+' movetime '+ANALYSIS_MOVETIME_MS);
    }
    function doStartEngineMove(){
      if(!engineReady || (game.game_over&&game.game_over()) || game.turn()===userColor) return;
      mode='play'; infoBuffer={}; setEngineState('Thinking…'); post({type:'analysis',depth:0,lines:[]});
      setOption('MultiPV',1);
      posFen();
      if(playElo>=1320){
        setOption('UCI_LimitStrength','true');
        setOption('UCI_Elo',Math.min(3190,playElo));
      }else{
        setOption('UCI_LimitStrength','false');
      }
      setOption('Skill Level',skillLevel);
      postThinking(true); waitingForBestmove=true; armWatchdog('play');
      engine.postMessage('go movetime '+PLAY_MOVETIME_MS+' depth '+PLAY_MAX_DEPTH);
    }
    function startAnalysis(){
      if(!engineReady) return;
      if(waitingForBestmove){ pendingNext='analyze'; stopEngine(); return; }
      doStartAnalysis();
    }
    function startEngineMove(){
      if(!engineReady || (game.game_over&&game.game_over()) || game.turn()===userColor) return;
      if(waitingForBestmove){ pendingNext='play'; stopEngine(); return; }
      doStartEngineMove();
    }
    function flushPendingNext(){
      var next=pendingNext; pendingNext=null;
      if(next==='analyze') doStartAnalysis(); else if(next==='play') doStartEngineMove(); else if(next==='candidate') doCandidateSearch();
    }
    function ensureEngineToMove(){
      if(!engineReady || waitingForBestmove || (game.game_over&&game.game_over()) || game.turn()===userColor) return;
      if(practiceActive&&practiceInBook&&practicePtr<practiceLine.length) playBookMove();
      else { if(practiceActive&&practiceInBook){practiceInBook=false;postPracticeGuide();} startEngineMove(); }
    }
    function scheduleEngineReply(){
      if(coachReplyTimer){clearTimeout(coachReplyTimer);coachReplyTimer=null;}
      var elapsed=Date.now()-coachUserMoveTs;
      var wait=Math.max(COACH_MIN_REPLY_MS,COACH_WINDOW_MS-elapsed);
      coachReplyTimer=setTimeout(function(){coachReplyTimer=null;coachReplyArmed=false;coachReplyReleased=false;ensureEngineToMove();},wait);
    }
    function armCoachReply(){
      coachReplyArmed=true;
      coachReplyReleased=false;
      coachUserMoveTs=Date.now();
      startAnalysis();
      // Safety valve: Stockfish must always answer the player's move.
      setTimeout(function(){
        if(game.turn()!==userColor && !(game.game_over&&game.game_over())){
          coachReplyReleased=true;
          if(!waitingForBestmove){
            if(coachReplyTimer){clearTimeout(coachReplyTimer);coachReplyTimer=null;}
            coachReplyArmed=false;
            coachReplyReleased=false;
            ensureEngineToMove();
          }
        }
      },4500);
    }

    function snapshotBest(){
      var info=infoBuffer[1]; if(!info) return null;
      var pvSan=pvToSanArray(game.fen(),info.pvUci,1);
      return {cp:info.cp==null?null:info.cp,mate:info.mate==null?null:info.mate,san:pvSan[0]||null};
    }
    function tryParseCandidate(tmp,token){
      var raw=String(token||'').trim(); if(!raw) return null;
      try { var m=tmp.move(raw,{sloppy:true}); if(m) return m; } catch(e) {}
      var um=raw.replace(/\s+/g,'').toLowerCase();
      if(/^[a-h][1-8][a-h][1-8][qrbn]?$/.test(um)){
        try { var u=tmp.move({from:um.slice(0,2),to:um.slice(2,4),promotion:um[4]||'q'}); if(u) return u; } catch(e) {}
      }
      return null;
    }
    function startCandidateEval(ctx){ candidateCtx=ctx; if(!engineReady){post({type:'candidateEval',reqId:ctx.reqId,error:'engine_not_ready'});candidateCtx=null;return;} if(waitingForBestmove){pendingNext='candidate';stopEngine();return;} doCandidateSearch(); }
    function doCandidateSearch(){ if(!candidateCtx)return; mode='evalcand';infoBuffer={};setOption('Skill Level',20);setOption('MultiPV',1);engine.postMessage('position fen '+candidateCtx.fenAfter);waitingForBestmove=true;armWatchdog('analyze');engine.postMessage('go depth '+ANALYSIS_DEPTH+' movetime '+ANALYSIS_MOVETIME_MS); }
    function finishCandidate(){
      var ctx=candidateCtx;candidateCtx=null;var info=infoBuffer[1]||{};var afterCp=info.cp==null?null:-info.cp;var afterMate=info.mate==null?null:-info.mate;var best=ctx.best||null;var cpLoss=null;
      if(best&&best.cp!=null&&afterCp!=null)cpLoss=best.cp-afterCp;
      post({type:'candidateEval',reqId:ctx.reqId,san:ctx.san,bestSan:best?best.san:null,bestCp:best?best.cp:null,bestMate:best?best.mate:null,afterCp:afterCp,afterMate:afterMate,cpLoss:cpLoss});
    }

    function cleanSanTok(s){ return String(s||'').replace(/[+#!?]+/g,'').trim(); }
    function postPracticeGuide(extra){
      if(practiceActive&&practiceInBook&&practicePtr<practiceLine.length) post(Object.assign({type:'practiceGuide',expected:practiceLine[practicePtr],ply:practicePtr,total:practiceLine.length,done:false},extra||{}));
      else if(practiceActive){practiceActive=false;practiceInBook=false;post({type:'practiceGuide',done:true});}
    }
    function isPracticeUserMoveExpected(mv){ return !(practiceActive&&practiceInBook&&practicePtr<practiceLine.length) || cleanSanTok(mv&&mv.san)===cleanSanTok(practiceLine[practicePtr]); }
    function keepPracticeMoveOrUndo(mv){
      if(isPracticeUserMoveExpected(mv))return true;
      var played=(mv&&mv.san)||'',expected=practiceLine[practicePtr]||'';
      try{game.undo();}catch(e){} clearHL();selected=null;board.position(game.fen());postTurn();post({type:'analysis',depth:0,lines:[]});wrongCandidateCtx={san:played,expected:expected};post({type:'wrongMoveCandidate',san:played,expected:expected});if(engineReady&&game.turn()===userColor)startAnalysis();return false;
    }
    function engineRespond(){ if(practiceActive&&practiceInBook&&practicePtr<practiceLine.length)playBookMove();else{if(practiceActive&&practiceInBook){practiceInBook=false;postPracticeGuide();}startEngineMove();} }
    function playBookMove(){
      var san=practiceLine[practicePtr],m=null;try{m=game.move(san,{sloppy:true});}catch(e){}
      if(!m){practiceInBook=false;startEngineMove();return;}practicePtr++;board.position(game.fen());setTimeout(function(){afterMoveBookkeeping('engine',m,{book:true});},350);
    }
    function afterMoveBookkeeping(by,mv,meta){
      post(Object.assign({type:'move',by:by,san:mv&&mv.san?mv.san:'',fen:game.fen(),pgn:currentPgn(),ply:currentPly(),userColor:userColor},meta||{}));postTurn();
      if(by==='user'){pendingUserMoveSan=mv&&mv.san?mv.san:'';redoStack=[];if(practiceActive&&practiceInBook&&practicePtr<practiceLine.length&&cleanSanTok(mv&&mv.san)===cleanSanTok(practiceLine[practicePtr]))practicePtr++;}
      if(postGameOverIfAny())return;
      if(game.turn()===userColor){startAnalysis();if(practiceActive)postPracticeGuide();}
      else if(by==='user'){
        // Never block Stockfish's reply behind Hood/TTS analysis.
        clearCoachReply();
        setTimeout(function(){
          if(game.turn()!==userColor && !(game.game_over&&game.game_over())) startEngineMove();
        },260);
      }
      else if(practiceActive&&practiceInBook&&practicePtr<practiceLine.length)playBookMove();
      else{if(practiceActive&&practiceInBook){practiceInBook=false;postPracticeGuide();}startEngineMove();}
    }

    function attachEngineHandlers(){
      engine.onerror=function(err){setEngineState('Engine error');post({type:'engineError',message:(err&&err.message)||String(err)});};
      engine.onmessage=function(e){
        var line=e&&e.data?String(e.data):'';
        if(/uciok/.test(line)){setEngineState('Almost ready…');engine.postMessage('isready');return;}
        if(/readyok/.test(line)){
          engineReady=true;
          if(engineBootTimer){clearTimeout(engineBootTimer);engineBootTimer=null;}
          setEngineState('Stockfish');post({type:'boardReady'});postTurn();
          if(practiceActive){if(game.turn()===userColor){startAnalysis();postPracticeGuide();}else engineRespond();}
          else if(game.turn()===userColor)startAnalysis();else startEngineMove();return;
        }
        if(line.indexOf('info ')===0){
          if(mode!=='analyze'&&mode!=='evalcand')return;
          var mvp=line.match(/\bmultipv (\d+)/),depthM=line.match(/\bdepth (\d+)/),cpM=line.match(/\bscore cp (-?\d+)/),mateM=line.match(/\bscore mate (-?\d+)/),pvM=line.match(/\bpv (.+)$/);
          var rank=mvp?parseInt(mvp[1],10):1;if(!depthM||rank<1||rank>3)return;var depth=parseInt(depthM[1],10),prev=infoBuffer[rank]||{};if(prev.depth&&depth<prev.depth-2)return;
          infoBuffer[rank]={depth:depth,cp:cpM?parseInt(cpM[1],10):null,mate:mateM?parseInt(mateM[1],10):null,pvUci:pvM?pvM[1]:(prev.pvUci||'')};if(mode==='analyze')emitAnalysis(false);return;
        }
        if(line.indexOf('bestmove')===0){
          if(staleBestmoves>0){staleBestmoves--;return;}waitingForBestmove=false;clearWatchdog();watchdogRetries=0;post({type:'engineOk'});
          if(pendingNext){flushPendingNext();return;}
          if(mode==='evalcand'){finishCandidate();mode='idle';if(game.turn()===userColor)startAnalysis();else ensureEngineToMove();return;}
          if(mode==='analyze'){
            emitAnalysis(true,true);var top=infoBuffer[1]||null;
            if(game.turn()===userColor){if(top){var pvSan=pvToSanArray(game.fen(),top.pvUci,1);lastUserTurnEval={cp:top.cp==null?null:top.cp,mate:top.mate==null?null:top.mate,san:pvSan[0]||null};}}
            else if(coachReplyArmed&&pendingUserMoveSan){
              var before=lastUserTurnEval||{},afterCp=top&&top.cp!=null?-top.cp:null,afterMate=top&&top.mate!=null?-top.mate:null,cpLoss=before.cp!=null&&afterCp!=null?Math.max(0,before.cp-afterCp):null;
              var reviewFen=game.fen(),reviewPgn=currentPgn(),reviewPly=currentPly(),reviewPv=top?pvToSanArray(reviewFen,top.pvUci,6):[];
              post({type:'moveReview',reviewId:String(reviewPly)+':'+String(pendingUserMoveSan||'')+':'+String(reviewFen||''),san:pendingUserMoveSan,pgn:reviewPgn,ply:reviewPly,cpLoss:cpLoss,beforeCp:before.cp==null?null:before.cp,afterCp:afterCp,beforeMate:before.mate==null?null:before.mate,afterMate:afterMate,evaluation:afterMate!=null?'mate='+afterMate:(afterCp!=null?'cp='+afterCp:''),bestMove:before.san||null,pv:reviewPv,depth:top&&top.depth?top.depth:ANALYSIS_DEPTH,fen:reviewFen,userColor:userColor});pendingUserMoveSan='';
            }
            mode='idle';
            if(coachReplyArmed&&game.turn()!==userColor&&!(game.game_over&&game.game_over())){if(coachReplyReleased){coachReplyArmed=false;coachReplyReleased=false;ensureEngineToMove();}else scheduleEngineReply();}else ensureEngineToMove();return;
          }
          if(mode==='play'){
            var parts=line.split(' '),uci=parts[1]||'';postThinking(false);setEngineState('Stockfish');
            if(uci.length>=4){var m=game.move({from:uci.slice(0,2),to:uci.slice(2,4),promotion:uci[4]||'q'});if(m){board.position(game.fen());afterMoveBookkeeping('engine',m);return;}}
            mode='idle';ensureEngineToMove();return;
          }
          ensureEngineToMove();
        }
      };
    }

    var engineBootAttempts=0;
    var engineBootTimer=null;
    function initEngine(){
      engineBootAttempts++;
      try {
        if(engineBootTimer){clearTimeout(engineBootTimer);engineBootTimer=null;}
        engineReady=false;
        setEngineState(engineBootAttempts>1?'Retrying…':'Loading…');

        try{if(engine)engine.terminate();}catch(e){}
        engine=null;

        // Run the single-threaded Stockfish build directly as a dedicated worker.
        // IMPORTANT: do not append ",worker" to the URL hash. In this Stockfish.js
        // build that flag is used internally for nested workers and prevents the
        // main dedicated-worker bootstrap from running.
        engine = new Worker(STOCKFISH_JS);
        attachEngineHandlers();

        engine.onerror=function(err){
          if(engineBootAttempts<3){
            try{engine.terminate();}catch(e){}
            setTimeout(initEngine,450);
            return;
          }
          setEngineState('Engine error');
          post({type:'engineError',message:(err&&err.message)||'Stockfish worker failed to start.'});
        };

        // The worker queues UCI commands until WASM is ready.
        setEngineState('Starting…');
        engine.postMessage('uci');

        engineBootTimer=setTimeout(function(){
          if(engineReady)return;
          if(engineBootAttempts<3){
            try{engine.terminate();}catch(e){}
            setTimeout(initEngine,250);
            return;
          }
          setEngineState('Engine error');
          post({type:'engineError',message:'Stockfish could not start on this device.'});
        },8000);
      } catch(error){
        if(engineBootAttempts<3){
          setTimeout(initEngine,450);
          return;
        }
        setEngineState('Engine error');
        post({type:'engineError',message:String(error&&error.message||error)});
      }
    }

    var overlay=document.getElementById('promoOverlay'),pendingFrom=null,pendingTo=null,pendingColor=null;
    function isPromo(from,to){var p=game.get(from);if(!p||p.type!=='p')return false;return(p.color==='w'&&to[1]==='8')||(p.color==='b'&&to[1]==='1');}
    function showPromo(c){overlay.classList.add('show');overlay.querySelectorAll('img').forEach(function(img){var L=img.getAttribute('data-piece');img.src=PIECE_BASE+(c==='w'?'w':'b')+L.toUpperCase()+'.png';});}
    function hidePromo(){overlay.classList.remove('show');}
    overlay.addEventListener('click',function(e){var img=e.target.closest('img[data-piece]');if(!img){hidePromo();pendingFrom=pendingTo=pendingColor=null;return;}var p=img.getAttribute('data-piece').toLowerCase();hidePromo();if(pendingFrom&&pendingTo){var mv=game.move({from:pendingFrom,to:pendingTo,promotion:p});pendingFrom=pendingTo=pendingColor=null;if(mv){if(!keepPracticeMoveOrUndo(mv))return;board.position(game.fen());afterMoveBookkeeping('user',mv);}}});

    function clearHL(){ $('#myBoard .square-55d63').removeClass('selected target'); }
    function hlTargets(from){try{(game.moves({square:from,verbose:true})||[]).forEach(function(m){$('#myBoard .square-'+m.to).addClass('target');});}catch(e){}}
    var selected=null;
    var board=Chessboard('myBoard',{
      position:game.fen(),orientation:'white',draggable:true,pieceTheme:PIECE_BASE+'{piece}.png',showNotation:false,
      onDragStart:function(src){if(game.game_over())return false;if(game.turn()!==userColor)return false;var p=game.get(src);return !!p&&p.color===userColor;},
      onDrop:function(source,target){if(!target||target==='offboard'||target===source||game.turn()!==userColor)return 'snapback';if(isPromo(source,target)){pendingFrom=source;pendingTo=target;pendingColor=game.get(source).color;showPromo(pendingColor);return 'snapback';}var move=game.move({from:source,to:target,promotion:'q'});if(move==null)return 'snapback';if(!keepPracticeMoveOrUndo(move))return 'snapback';clearHL();selected=null;setTimeout(function(){board.position(game.fen());},0);afterMoveBookkeeping('user',move);},
      onSnapEnd:function(){board.position(game.fen());}
    });

    function squareAtPoint(x,y){var el=document.elementFromPoint(x,y);if(!el)return null;if((el.className||'').indexOf('piece-417db')>=0){var old=el.style.visibility;el.style.visibility='hidden';var under=document.elementFromPoint(x,y);el.style.visibility=old;el=under||el;}while(el&&(el.className||'').indexOf('square-55d63')<0)el=el.parentElement;if(!el)return null;var ds=el.getAttribute&&el.getAttribute('data-square');if(ds)return ds;var c=(el.className||'').split(/\s+/);for(var i=0;i<c.length;i++)if(/^square-[a-h][1-8]$/.test(c[i]))return c[i].split('-')[1];return null;}
    var lastTap={ts:0,sq:null};
    function handleTap(e){var pt=e&&e.changedTouches?e.changedTouches[0]:e;if(!pt)return;var sq=squareAtPoint(pt.clientX,pt.clientY);if(!sq)return;var now=Date.now();if(lastTap.sq===sq&&now-lastTap.ts<200)return;lastTap={ts:now,sq:sq};if(game.turn()!==userColor)return;if(!selected){var p=game.get(sq);if(!p||p.color!==userColor)return;selected=sq;clearHL();$('#myBoard .square-'+sq).addClass('selected');hlTargets(sq);return;}if(selected===sq){clearHL();selected=null;return;}if(isPromo(selected,sq)){pendingFrom=selected;pendingTo=sq;pendingColor=game.get(selected).color;showPromo(pendingColor);return;}var mv=game.move({from:selected,to:sq,promotion:'q'});if(!mv){var q=game.get(sq);if(q&&q.color===userColor){selected=sq;clearHL();$('#myBoard .square-'+sq).addClass('selected');hlTargets(sq);}return;}selected=null;clearHL();if(!keepPracticeMoveOrUndo(mv))return;board.position(game.fen());afterMoveBookkeeping('user',mv);}
    var elBoard=document.getElementById('myBoard');if(IS_TOUCH)elBoard.addEventListener('touchend',function(e){e.preventDefault();handleTap(e);},{passive:false});else elBoard.addEventListener('click',handleTap);

    window.evalCandidateFromWeb=function(token,reqId){try{if(!engineReady){post({type:'candidateEval',reqId:reqId,error:'engine_not_ready'});return;}if(game.turn()!==userColor){post({type:'candidateEval',reqId:reqId,notYourTurn:true});return;}var tmp=new Chess(game.fen()),mv=tryParseCandidate(tmp,token);if(!mv){post({type:'candidateEval',reqId:reqId,illegal:true});return;}startCandidateEval({reqId:reqId,fenAfter:tmp.fen(),san:mv.san,best:snapshotBest()});}catch(e){post({type:'candidateEval',reqId:reqId,error:String(e&&e.message||e)});}};
    window.releaseEngineReplyFromWeb=function(){if(!coachReplyArmed&&!coachReplyTimer)return;coachReplyReleased=true;if(!waitingForBestmove){if(coachReplyTimer){clearTimeout(coachReplyTimer);coachReplyTimer=null;}coachReplyArmed=false;coachReplyReleased=false;ensureEngineToMove();}};
    window.startPracticeFromWeb=function(moves,color,startFen){try{hardResetSearchState();userColor=color==='b'?'b':'w';board.orientation(userColor==='w'?'white':'black');refreshCoords();game=new Chess(startFen||START_FEN);clearHL();selected=null;board.position(game.fen());redoStack=[];practiceActive=true;practiceLine=Array.isArray(moves)?moves:[];practicePtr=0;practiceInBook=practiceLine.length>0;postTurn();post({type:'analysis',depth:0,lines:[]});if(engineReady){if(game.turn()===userColor){startAnalysis();postPracticeGuide();}else engineRespond();}}catch(e){post({type:'practiceError',message:String(e&&e.message||e)});}};
    window.resolveWrongCandidateFromWeb=function(accept){var ctx=wrongCandidateCtx;wrongCandidateCtx=null;if(!ctx)return;if(!accept){postPracticeGuide({wrong:true,played:ctx.san});return;}var m=null;try{m=game.move(ctx.san,{sloppy:true});}catch(e){}if(!m){postPracticeGuide({wrong:true,played:ctx.san});return;}practiceInBook=false;clearHL();selected=null;board.position(game.fen());afterMoveBookkeeping('user',m);};
    window.loadFenFromWeb=function(fen,color){try{hardResetSearchState();userColor=color==='b'?'b':'w';board.orientation(userColor==='w'?'white':'black');refreshCoords();game=new Chess(fen||START_FEN);practiceActive=false;practiceInBook=false;practiceLine=[];practicePtr=0;redoStack=[];clearHL();selected=null;board.position(game.fen());postTurn();post({type:'analysis',depth:0,lines:[]});if(engineReady){if(game.turn()===userColor)startAnalysis();else startEngineMove();}}catch(e){post({type:'engineError',message:String(e&&e.message||e)});}};
    window.undoMoveFromWeb=function(){try{hardResetSearchState();var mv=game.undo();if(!mv)return;redoStack.push({from:mv.from,to:mv.to,promotion:mv.promotion||'q'});practiceActive=false;practiceInBook=false;practiceLine=[];practicePtr=0;clearHL();selected=null;board.position(game.fen());postTurn();post({type:'analysis',depth:0,lines:[]});if(engineReady&&game.turn()===userColor)startAnalysis();}catch(e){}};
    window.redoMoveFromWeb=function(){try{hardResetSearchState();var mv=redoStack.pop();if(!mv)return;game.move(mv);practiceActive=false;practiceInBook=false;practiceLine=[];practicePtr=0;clearHL();selected=null;board.position(game.fen());postTurn();post({type:'analysis',depth:0,lines:[]});if(engineReady){if(game.turn()===userColor)startAnalysis();else ensureEngineToMove();}}catch(e){}};
    window.resumePlayFromWeb=function(){try{redoStack=[];clearCoachReply();pendingNext=null;candidateCtx=null;stopEngine();mode='idle';infoBuffer={};post({type:'analysis',depth:0,lines:[]});if(engineReady){if(postGameOverIfAny())return;if(game.turn()===userColor)startAnalysis();else ensureEngineToMove();}}catch(e){}};
    window.resetToStartFromWeb=function(){practiceActive=false;practiceInBook=false;practiceLine=[];practicePtr=0;redoStack=[];hardResetSearchState();game=new Chess(START_FEN);clearHL();selected=null;board.position(game.fen());postTurn();if(engineReady){if(game.turn()===userColor)startAnalysis();else startEngineMove();}};
    window.setUserColorFromWeb=function(c){hardResetSearchState();userColor=c==='b'?'b':'w';board.orientation(userColor==='w'?'white':'black');refreshCoords();game=new Chess(START_FEN);clearHL();selected=null;board.position(game.fen());postTurn();if(engineReady){if(game.turn()===userColor)startAnalysis();else startEngineMove();}};
    window.switchSideFromWeb=function(c){hardResetSearchState();userColor=c==='b'?'b':'w';board.orientation(userColor==='w'?'white':'black');refreshCoords();practiceActive=false;practiceInBook=false;practiceLine=[];practicePtr=0;redoStack=[];clearHL();selected=null;board.position(game.fen());postTurn();post({type:'analysis',depth:0,lines:[]});if(engineReady){if(game.turn()===userColor)startAnalysis();else startEngineMove();}};
    window.setEloFromWeb=function(n){
      playElo=Math.max(800,Math.min(3200,Math.round(Number(n)||1600)));
      if(playElo<1320){
        skillLevel=Math.max(0,Math.min(4,Math.round((playElo-800)/130)));
      }else{
        skillLevel=Math.max(0,Math.min(20,Math.round((playElo-1320)/94)));
      }
    };
    window.setMultiPvFromWeb=function(n){multiPV=Math.max(1,Math.min(3,Number(n)||1));if(mode==='analyze')startAnalysis();};

    refreshCoords();
    setTimeout(resizeVisualBoard,80);setTimeout(resizeVisualBoard,260);
    initEngine();
  })();
  </script>
</body>
</html>`;
}

function HoodEngineBoardWeb(
  {
    onCoachHint,
    onReady,
    onEngineError,
    onSideChange,
    onPracticeGuide,
    onPracticeWrongCandidate,
    boardSize = 620,
  },
  ref,
) {
  const iframeRef = useRef(null);
  const [ready, setReady] = useState(false);
  const html = useMemo(() => buildBoardHtml(), []);

  const callBoard = useCallback((name, ...args) => {
    try {
      const win = iframeRef.current?.contentWindow;
      const fn = win?.[name];
      if (typeof fn === "function") return fn(...args);
    } catch (error) {
      onEngineError?.(error?.message || error);
    }
    return undefined;
  }, [onEngineError]);

  useImperativeHandle(ref, () => ({
    startPractice(moves, color, fen = START_FEN) {
      const side = color === "b" ? "b" : "w";
      onSideChange?.(side);
      callBoard("startPracticeFromWeb", Array.isArray(moves) ? moves : [], side, fen || START_FEN);
    },
    loadFen(fen, color = "w") {
      const side = color === "b" ? "b" : "w";
      onSideChange?.(side);
      callBoard("loadFenFromWeb", fen || START_FEN, side);
    },
    resolveWrongCandidate(accept) {
      callBoard("resolveWrongCandidateFromWeb", Boolean(accept));
    },
    releaseEngineReply() {
      callBoard("releaseEngineReplyFromWeb");
    },
    undoMove() {
      callBoard("undoMoveFromWeb");
    },
    redoMove() {
      callBoard("redoMoveFromWeb");
    },
    resumePlay() {
      callBoard("resumePlayFromWeb");
    },
    resetToStart() {
      callBoard("resetToStartFromWeb");
    },
    setUserColor(color) {
      const side = color === "b" ? "b" : "w";
      onSideChange?.(side);
      callBoard("setUserColorFromWeb", side);
    },
    switchSide(color) {
      const side = color === "b" ? "b" : "w";
      onSideChange?.(side);
      callBoard("switchSideFromWeb", side);
    },
    setElo(elo) {
      callBoard("setEloFromWeb", Math.max(800, Math.min(3200, Math.round(Number(elo) || 1600))));
    },
    setMultiPv(value) {
      callBoard("setMultiPvFromWeb", value);
    },
    evaluateCandidate(token, reqId = `web-${Date.now()}`) {
      return callBoard("evalCandidateFromWeb", token, reqId);
    },
  }), [callBoard, onSideChange]);

  useEffect(() => {
    const handleMessage = (event) => {
      if (event.source !== iframeRef.current?.contentWindow) return;
      if (!event.data?.__hoodBoard) return;
      const msg = event.data?.payload || {};
      const type = msg?.type;
      if (type === "ready" || type === "boardReady") {
        setReady(true);
        onReady?.(msg);
        return;
      }
      if (type === "practiceGuide") {
        onPracticeGuide?.(msg);
        return;
      }
      if (type === "practiceWrongCandidate" || type === "wrongMoveCandidate") {
        onPracticeWrongCandidate?.(msg);
        return;
      }
      if (["analysis", "move", "moveReview", "gameOver", "turn", "thinking", "candidateEval"].includes(type)) {
        onCoachHint?.(msg);
        return;
      }
      if (type === "sideChange") {
        onSideChange?.(msg?.side === "b" ? "b" : "w");
        return;
      }
      if (type === "engineError" || type === "practiceError") {
        onEngineError?.(msg?.message || "Engine error");
      }
    };
    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [onCoachHint, onEngineError, onPracticeGuide, onPracticeWrongCandidate, onReady, onSideChange]);

  const side = Math.max(260, Math.min(Number(boardSize) || 620, 900));

  return (
    <div className="hood-engine-board-shell" style={{ width: "100%", display: "flex", justifyContent: "center", background: "transparent" }}>
      <style>{`
        .hood-engine-board-shell{width:100%}
        .hood-engine-board-box{width:min(100%, ${side}px);aspect-ratio:1/1;position:relative}
        @media (max-width:768px){
          .hood-engine-board-shell{width:100vw;max-width:none}
          .hood-engine-board-box{width:100vw;max-width:none}
        }
      `}</style>
      <div className="hood-engine-board-box">
        <iframe
          ref={iframeRef}
          title="Hood AI chessboard"
          srcDoc={html}
          onLoad={() => setReady(false)}
          style={{
            display: "block",
            width: "100%",
            height: "100%",
            border: 0,
            background: "transparent",
            opacity: ready ? 1 : 0.98,
          }}
          allow="cross-origin-isolated"
        />
      </div>
    </div>
  );
}

export default forwardRef(HoodEngineBoardWeb);
